"use client";

import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Button, Field, FieldError, FieldLabel, Input } from "@stocks/ui";
import type { ProductDetailResponse } from "@stocks/shared";

import { useUpdateProduct } from "../model/useUpdateProduct";

interface Values {
  name: string;
  description: string;
  isActive: boolean;
}

const toValues = (p: ProductDetailResponse): Values => ({
  name: p.name,
  description: p.description ?? "",
  isActive: p.isActive,
});

/** 제품 기본 정보(이름·설명·사용 여부) 수정 */
export const ProductEditForm = ({ product }: { product: ProductDetailResponse }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<Values>({ defaultValues: toValues(product) });
  const update = useUpdateProduct();

  // 저장/재조회로 서버 값이 바뀌면 폼을 맞춘다
  useEffect(() => reset(toValues(product)), [product, reset]);

  const onSubmit = (v: Values) =>
    update.mutate({
      id: product.id,
      values: { name: v.name.trim(), description: v.description.trim(), isActive: v.isActive },
    });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
      <div className='grid gap-4 sm:grid-cols-2'>
        <Field>
          <FieldLabel htmlFor='product-edit-name'>제품명</FieldLabel>
          <Input
            id='product-edit-name'
            {...register("name", { required: "제품명을 입력하세요." })}
          />
          {errors.name && <FieldError>{errors.name.message}</FieldError>}
        </Field>
        <Field>
          <FieldLabel htmlFor='product-edit-description'>설명</FieldLabel>
          <Input id='product-edit-description' {...register("description")} />
        </Field>
      </div>
      <label className='flex items-center gap-2 text-sm'>
        <input type='checkbox' {...register("isActive")} />
        사용 중 (해제하면 발주 입력 목록에서 숨김)
      </label>
      <div>
        <Button type='submit' disabled={!isDirty || update.isPending}>
          {update.isPending ? "저장 중…" : "정보 저장"}
        </Button>
      </div>
    </form>
  );
};
